import { useQuery } from "@apollo/client";
import { Box, Card, Flex, H3, Loading } from "orcs-design-system";
import PropTypes from "prop-types";
import React, { useCallback } from "react";
import FteChart from "src/allocation/pages/ForecastPageDynamic/components/Modals/ForecastFteChartModal/FteChart";
import {
  useColumns,
  useForecastModel,
  withForecastContextProvider,
} from "src/allocation/pages/ForecastPageDynamic/context/ForecastContext";
import useLoadIntersectingFte from "src/allocation/pages/ForecastPageDynamic/useLoadIntersectingFte";
import usePageMainQuery from "src/allocation/pages/ForecastPageDynamic/usePageMainQuery";
import usePageSecondQuery from "src/allocation/pages/ForecastPageDynamic/usePageSecondQuery";
import { useFeatureFlags } from "src/contexts/global/FeatureFlagsContext";
import { useAllocationProject } from "src/contexts/global/GlobalContext";
import { useWorkspace } from "src/contexts/global/WorkspaceContext";
import { getTeamDetailsQuery } from "src/queries/group.graphql";

import { useAIDashboardStore } from "../context/AIDashboardContext";
import useCoordinatedChartMax from "../hooks/useCoordinatedChartMax";

// Inner component that uses the ForecastContext hooks
const ForecastChartContent = withForecastContextProvider(
  ({ groupName, dashboardChannel }) => {
    usePageMainQuery();
    usePageSecondQuery();
    useLoadIntersectingFte();

    const columns = useColumns();
    const model = useForecastModel();

    // Get the grouping data
    const rootGroup = model?.rootGroup;
    const dynamicModel = model?.dynamicModel;
    const groupingLookup = dynamicModel?.lookups?.groupingLookup || {};
    const grouping = rootGroup ? groupingLookup[rootGroup.id] : null;

    // Calculate max value from chart data - wrapped in useCallback to prevent infinite loops
    const calculateMax = useCallback(() => {
      if (!grouping || !columns?.length) {
        return 0;
      }

      const {
        summedTotalFtes = [],
        summedTotals = [],
        constraints = [],
      } = grouping;
      let max = 0;

      columns.forEach((_, index) => {
        const fte = summedTotalFtes[index] || 0;
        const forecast = summedTotals[index]?.total || 0;
        const constraint = constraints[index]?.constraint || 0;

        max = Math.max(max, fte, forecast, constraint);
      });

      return max;
    }, [grouping, columns]);

    // Use the coordinated chart max hook to sync Y-axis across multiple charts
    const maxChannelName = `${dashboardChannel}-max-forecast-display-value`;
    const { coordinatedMax } = useCoordinatedChartMax(
      maxChannelName,
      calculateMax
    );

    // Now handle early returns after all hooks
    if (!model?.rootGroup) {
      return (
        <Flex justifyContent="center" alignItems="center" height="400px">
          <Loading large centered />
        </Flex>
      );
    }

    // Use the root grouping from the model - this already has all the data computed
    if (
      !rootGroup ||
      !columns?.length ||
      !grouping ||
      !grouping.summedTotalFtes ||
      !grouping.summedTotals
    ) {
      return (
        <Box p="r">
          <H3>No forecast data available</H3>
        </Box>
      );
    }

    return (
      <Box width="100%" p="r">
        <H3 mb="r">{`Workforce Forecast for ${
          groupName || rootGroup.name
        }`}</H3>
        <FteChart
          grouping={grouping}
          columns={columns}
          maxYValue={coordinatedMax}
        />
      </Box>
    );
  }
);

ForecastChartContent.propTypes = {
  groupName: PropTypes.string,
  dashboardChannel: PropTypes.string.isRequired,
};

const CanvTeamForecastChart = ({ channel }) => {
  const store = useAIDashboardStore(channel);
  const groupId = store((state) => state.value?.id);
  const workspace = useWorkspace();
  const allocationProject = useAllocationProject();
  const featureFlags = useFeatureFlags();

  // Get reset function for the max value store
  const maxChannelName = `${channel}-max-forecast-display-value`;
  const maxStore = useAIDashboardStore(maxChannelName);
  const resetMax = maxStore((state) => state.publish);

  React.useEffect(() => {
    // Reset max value when groupId changes (e.g., new team selected)
    resetMax(null);
  }, [groupId, resetMax]);

  // Fetch group details
  const {
    data: groupData,
    loading: groupLoading,
    error: groupError,
  } = useQuery(getTeamDetailsQuery, {
    variables: { groupId },
    skip: !groupId,
  });

  const group = groupData?.getGroup;

  // No groupId selected state
  if (!groupId) {
    return (
      <Card style={{ padding: "16px", textAlign: "center", color: "#666" }}>
        Select a team to view forecast
      </Card>
    );
  }

  // Loading state
  if (groupLoading || !group) {
    return (
      <Flex justifyContent="center" alignItems="center" height="400px">
        <Loading large centered />
      </Flex>
    );
  }

  // Error state
  if (groupError) {
    return (
      <Box p="r">
        <H3 color="danger">Error loading forecast data</H3>
        <p>{groupError.message}</p>
      </Box>
    );
  }

  // Wrap with ForecastContextProvider to reuse all existing logic
  return (
    <ForecastChartContent
      group={group}
      workspace={workspace}
      pageMode="forecast"
      allocationProject={allocationProject}
      featureFlags={featureFlags}
      groupName={group.name}
      dashboardChannel={channel}
    />
  );
};

CanvTeamForecastChart.propTypes = {
  channel: PropTypes.string.isRequired,
};

export default CanvTeamForecastChart;
